import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { api, ApiError, DatasetDefinition } from "../../lib/api.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

function statusPill(status: DatasetDefinition["status"]) {
  switch (status) {
    case "active":   return <span className="pill pill-ok">Active</span>;
    case "draft":    return <span className="pill pill-info">Draft</span>;
    case "archived": return <span className="pill pill-muted">Archived</span>;
    default:         return <span className="pill pill-muted">{status}</span>;
  }
}

function sensitivityPill(s: DatasetDefinition["sensitivity"]) {
  switch (s) {
    case "low":        return <span className="pill pill-muted">Low</span>;
    case "moderate":   return <span className="pill pill-warn">Moderate</span>;
    case "restricted": return <span className="pill pill-err">Restricted</span>;
    default:           return <span className="pill pill-muted">{s}</span>;
  }
}

function fmt(date?: string): string {
  return date ? new Date(date).toLocaleString() : "—";
}

export default function AdminDatasets() {
  const [datasets, setDatasets] = useState<DatasetDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sensitivityFilter, setSensitivityFilter] = useState<string>("all");
  const [domainFilter, setDomainFilter] = useState<string>("all");

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const { datasets } = await api.datasets();
      setDatasets([...(datasets ?? [])].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const domains = useMemo(
    () => Array.from(new Set(datasets.map((d) => d.sourceDomain || "—"))).sort(),
    [datasets]
  );

  const filtered = useMemo(() => {
    return datasets.filter((d) => {
      if (statusFilter !== "all" && d.status !== statusFilter) return false;
      if (sensitivityFilter !== "all" && d.sensitivity !== sensitivityFilter) return false;
      if (domainFilter !== "all" && (d.sourceDomain || "—") !== domainFilter) return false;
      return true;
    });
  }, [datasets, statusFilter, sensitivityFilter, domainFilter]);

  const t = useTableState(filtered as unknown as Array<Record<string, unknown>>, {
    searchFields: ["name", "code", "sourceDomain", "owner", "status", "sensitivity"],
    initialPageSize: 25
  });

  const total = datasets.length;
  const active = datasets.filter((d) => d.status === "active").length;
  const publication = datasets.filter((d) => d.publicationEligible).length;
  const forwarding = datasets.filter((d) => d.forwardingEligible).length;
  const totalRows = datasets.reduce((acc, d) => acc + (d.rowCount || 0), 0);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Datasets</h1>
          <p>All datasets registered across workspaces. Datasets are derived from DHIS2 sources and can be exposed via portals or forwarded downstream.</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => void refresh()} disabled={loading}>Refresh</button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="section">
        <div className="stat-grid">
          <div className="stat"><div className="stat-label">Datasets</div><div className="stat-value">{total}</div></div>
          <div className="stat"><div className="stat-label">Active</div><div className="stat-value">{active}</div></div>
          <div className="stat"><div className="stat-label">Publication eligible</div><div className="stat-value">{publication}</div></div>
          <div className="stat"><div className="stat-label">Forwarding eligible</div><div className="stat-value">{forwarding}</div></div>
          <div className="stat"><div className="stat-label">Rows (sum)</div><div className="stat-value">{totalRows.toLocaleString()}</div></div>
        </div>
      </section>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label className="muted" style={{ fontSize: 12 }}>Status</label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
        <label className="muted" style={{ fontSize: 12, marginLeft: 12 }}>Sensitivity</label>
        <select value={sensitivityFilter} onChange={(e) => setSensitivityFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="low">Low</option>
          <option value="moderate">Moderate</option>
          <option value="restricted">Restricted</option>
        </select>
        <label className="muted" style={{ fontSize: 12, marginLeft: 12 }}>Source domain</label>
        <select value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)}>
          <option value="all">All ({domains.length})</option>
          {domains.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {loading && datasets.length === 0 ? (
        <SkeletonTable rows={5} columns={4} />
      ) : filtered.length === 0 ? (
        <div className="empty">
          <p>No datasets match the current filters.</p>
        </div>
      ) : (
        <div>
          <TableToolbar
            hideExport
            search={t.search}
            setSearch={t.setSearch}
            page={t.page}
            setPage={t.setPage}
            pageSize={t.pageSize}
            setPageSize={t.setPageSize}
            totalPages={t.totalPages}
            totalRows={t.totalRows}
            searchPlaceholder="Search dataset name / code / owner…"
          />
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Type</th>
                <th>Source domain</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Sensitivity</th>
                <th>Rows</th>
                <th>Eligible for</th>
                <th>Last refreshed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as DatasetDefinition[]).map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>{d.name}</strong>
                    {d.tags?.length ? (
                      <div className="soft" style={{ fontSize: 12 }}>{d.tags.join(", ")}</div>
                    ) : null}
                  </td>
                  <td className="soft"><code>{d.code}</code></td>
                  <td className="soft">{d.datasetType}</td>
                  <td className="soft">{d.sourceDomain || "—"}</td>
                  <td className="soft">{d.owner || "—"}</td>
                  <td>{statusPill(d.status)}</td>
                  <td>{sensitivityPill(d.sensitivity)}</td>
                  <td className="soft">{(d.rowCount ?? 0).toLocaleString()}</td>
                  <td>
                    {d.publicationEligible && <span className="pill pill-info" style={{ marginRight: 4 }}>Portal</span>}
                    {d.forwardingEligible && <span className="pill pill-info">Forwarding</span>}
                    {!d.publicationEligible && !d.forwardingEligible && <span className="soft">—</span>}
                  </td>
                  <td className="soft">{fmt(d.lastRefreshedAt)}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={async () => {
                          try {
                            const { dataset } = await api.refreshDataset(d.id);
                            setDatasets((prev) => prev.map((row) => (row.id === d.id ? dataset : row)));
                          } catch (e) {
                            setError(e instanceof ApiError ? e.message : String(e));
                          }
                        }}
                      >
                        Refresh
                      </button>
                      {d.status !== "archived" ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={async () => {
                            try {
                              const { dataset } = await api.setDatasetStatus(d.id, "archive");
                              setDatasets((prev) => prev.map((row) => (row.id === d.id ? dataset : row)));
                            } catch (e) {
                              setError(e instanceof ApiError ? e.message : String(e));
                            }
                          }}
                        >
                          Archive
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={async () => {
                            try {
                              const { dataset } = await api.setDatasetStatus(d.id, "activate");
                              setDatasets((prev) => prev.map((row) => (row.id === d.id ? dataset : row)));
                            } catch (e) {
                              setError(e instanceof ApiError ? e.message : String(e));
                            }
                          }}
                        >
                          Activate
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ color: "#b91c1c" }}
                        onClick={async () => {
                          if (!window.confirm(`Delete dataset "${d.name}" (${d.code})? This is permanent.`)) return;
                          try {
                            await api.deleteDataset(d.id);
                            setDatasets((prev) => prev.filter((row) => row.id !== d.id));
                          } catch (e) {
                            setError(e instanceof ApiError ? e.message : String(e));
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
